import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { LOGOS } from '@/lib/constants';

export default function IntroAnimation() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // Check if user has seen the intro this session
  const hasSeenIntro = typeof window !== 'undefined' && sessionStorage.getItem('hasSeenIntro') === 'true';
  
  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Only show on homepage, if not seen, and if motion is allowed
  const [show, setShow] = useState(isHomePage && !hasSeenIntro && !prefersReducedMotion);
  const [stage, setStage] = useState('hr-anexi'); // 'hr-anexi', 'transition', 'anexi-consulting'

  useEffect(() => {
    // Reset show state when location changes
    if (!isHomePage) {
      setShow(false);
      return;
    }
    
    if (show && isHomePage) {
      // Animation timeline
      const timer1 = setTimeout(() => setStage('transition'), 2500); // Show HR Anexi for 2.5s
      const timer2 = setTimeout(() => setStage('anexi-consulting'), 4000); // Transition at 4s
      const timer3 = setTimeout(() => {
        setShow(false);
        sessionStorage.setItem('hasSeenIntro', 'true');
      }, 7500); // Fade out at 7.5s
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [show, isHomePage]);

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeInOut' }}
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1A1008 0%, #2D1810 50%, #1A1008 100%)'
      }}
    >
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -100, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#3D1C11] rounded-full blur-3xl"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center">
        {/* STAGE 1: HR Anexi Logo */}
        {stage === 'hr-anexi' && (
          <motion.div
            initial={{ opacity: 1, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center"
          >
            <img 
              src={LOGOS.hrAnexiWhite}
              alt="HR Anexi"
              className="h-28 w-auto md:h-36 lg:h-44 drop-shadow-2xl"
            />
          </motion.div>
        )}

        {/* STAGE 2: Transition - "is now" text */}
        {stage === 'transition' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center"
          >
            <div className="text-white/70 text-lg md:text-xl lg:text-2xl font-light tracking-[0.4em] uppercase">
              IS NOW
            </div>
          </motion.div>
        )}

        {/* STAGE 3: Anexi Consulting Logo */}
        {stage === 'anexi-consulting' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center"
          >
            <motion.img 
              src={LOGOS.whiteColored}
              alt="Anexi Consulting"
              className="h-32 w-auto md:h-40 lg:h-52 drop-shadow-2xl mb-8"
              animate={{
                filter: [
                  'drop-shadow(0 0 0px rgba(232, 100, 44, 0))',
                  'drop-shadow(0 0 30px rgba(232, 100, 44, 0.4))',
                  'drop-shadow(0 0 0px rgba(232, 100, 44, 0))',
                ]
              }}
              transition={{
                duration: 2.5,
                ease: 'easeInOut'
              }}
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-white/60 text-sm md:text-base lg:text-lg font-light tracking-[0.25em] uppercase"
            >
              A new chapter begins
            </motion.p>
          </motion.div>
        )}
      </div>

      {/* Noise texture overlay */}
      <div className="noise-overlay absolute inset-0 pointer-events-none opacity-[0.015]" />
    </motion.div>
  );
}
